import { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import ReactSelect from "react-select";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Tabs,
  Tab,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Switch,
  Stack,
  CircularProgress,
  IconButton,
  FormHelperText,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useDispatch, useSelector } from "react-redux";
import { selectAnnouncementState } from "../../state/announcement/announcementSelector";
import { createAnnouncement, updateAnnouncement, resetCreateAnnouncement, resetUpdateAnnouncement } from "../../state/announcement/announcementSlice";
import CompanyListService from "../../services/companyList.service";
import AnnouncementService from "../../services/announcement.service";
import AnnouncementModalPreview from "./AnnouncementModalPreview";
import { showErrorToaster, showSuccessToaster } from "../../helpers/utils/toaster";

const formatDateTimeLocal = (d = new Date()) => {
  const date = new Date(d);
  const pad = (num) => String(num).padStart(2, "0");
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const getDefaultDates = () => {
  const nextMonth = new Date();
  nextMonth.setMonth(nextMonth.getMonth() + 1);
  return {
    expiresAt: formatDateTimeLocal(nextMonth),
  };
};

const DEFAULT_CONTENT = {
  title: "",
  message: "",
  buttonLabel: "Learn More",
  mediaType: "NONE",
  mediaUrl: "",
};

const LANGUAGES = [
  { code: "en", label: "English (EN)", shortLabel: "EN", defaultButtonLabel: "Learn More", isRequired: true },
  { code: "es", label: "Español (ES)", shortLabel: "ES", defaultButtonLabel: "Más información", isRequired: false },
  { code: "pt", label: "Português (PT)", shortLabel: "PT", defaultButtonLabel: "Saiba mais", isRequired: false },
  { code: "fr", label: "Français (FR)", shortLabel: "FR", defaultButtonLabel: "En savoir plus", isRequired: false },
];

const BUTTON_TARGET_OPTIONS = [
  { value: "INTERNAL", label: "Same Tab" },
  { value: "EXTERNAL", label: "New Tab" },
];

const reactSelectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  control: (base) => ({
    ...base,
    minHeight: 40,
    borderRadius: 6,
    borderColor: "#c4c4c4",
    "&:hover": { borderColor: "#333" },
  }),
};

const getInitialFormValues = () => {
  const { expiresAt } = getDefaultDates();
  return {
    status: "DRAFT",
    priority: 2,
    showButton: false,
    buttonUrl: "",
    buttonTargetType: "INTERNAL",
    expiresAt,
    allCompany: true,
    companyIds: [],
    content: {
      en: { ...DEFAULT_CONTENT, buttonLabel: "Learn More" },
      es: { ...DEFAULT_CONTENT, buttonLabel: "Más información" },
      pt: { ...DEFAULT_CONTENT, buttonLabel: "Saiba mais" },
      fr: { ...DEFAULT_CONTENT, buttonLabel: "En savoir plus" },
    },
  };
};

const CreateEditAnnouncementModal = ({ open, onClose, announcement, onSuccess }) => {
  const isEdit = Boolean(announcement?.id);

  const dispatch = useDispatch();
  const { isAdding, isUpdating, isAddSuccess, isUpdateSuccess } = useSelector(selectAnnouncementState);
  const submitting = isAdding || isUpdating;

  const [activeLangTab, setActiveLangTab] = useState("en");
  const [companies, setCompanies] = useState([]);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const { control, handleSubmit, reset, setValue, watch } = useForm({
    mode: "onChange",
    shouldUnregister: false,
    defaultValues: getInitialFormValues(),
  });

  const watchShowButton = watch("showButton");
  const watchAllCompany = watch("allCompany");
  const formValues = watch();

  useEffect(() => {
    CompanyListService.getCompanyList({ page: 1, perPage: 100 })
      .then((res) => {
        const list = res?.data?.data?.rows || res?.data?.rows || [];
        setCompanies(list);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (isAddSuccess || isUpdateSuccess) {
      if (isAddSuccess) dispatch(resetCreateAnnouncement());
      if (isUpdateSuccess) dispatch(resetUpdateAnnouncement());
      onSuccess?.();
      onClose();
    }
  }, [isAddSuccess, isUpdateSuccess, dispatch, onClose, onSuccess]);

  useEffect(() => {
    if (open) {
      if (announcement) {
        const defaultDates = getDefaultDates();
        reset({
          status: announcement.status || "DRAFT",
          priority: 2,
          showButton: Boolean(announcement.showButton),
          buttonUrl: announcement.buttonUrl || "",
          buttonTargetType: announcement.buttonTargetType || "INTERNAL",
          expiresAt: announcement.expiresAt ? formatDateTimeLocal(announcement.expiresAt) : defaultDates.expiresAt,
          allCompany: announcement.otherContent?.allCompany !== false,
          companyIds: announcement.otherContent?.companyIds || [],
          content: {
            en: {
              title: announcement.content?.en?.title || "",
              message: announcement.content?.en?.message || "",
              buttonLabel: announcement.content?.en?.buttonLabel || "Learn More",
              mediaType: announcement.content?.en?.mediaType || "NONE",
              mediaUrl: announcement.content?.en?.mediaUrl || "",
            },
            es: {
              title: announcement.content?.es?.title || "",
              message: announcement.content?.es?.message || "",
              buttonLabel: announcement.content?.es?.buttonLabel || "Más información",
              mediaType: announcement.content?.es?.mediaType || "NONE",
              mediaUrl: announcement.content?.es?.mediaUrl || "",
            },
            pt: {
              title: announcement.content?.pt?.title || "",
              message: announcement.content?.pt?.message || "",
              buttonLabel: announcement.content?.pt?.buttonLabel || "Saiba mais",
              mediaType: announcement.content?.pt?.mediaType || "NONE",
              mediaUrl: announcement.content?.pt?.mediaUrl || "",
            },
            fr: {
              title: announcement.content?.fr?.title || "",
              message: announcement.content?.fr?.message || "",
              buttonLabel: announcement.content?.fr?.buttonLabel || "En savoir plus",
              mediaType: announcement.content?.fr?.mediaType || "NONE",
              mediaUrl: announcement.content?.fr?.mediaUrl || "",
            },
          },
        });
      } else {
        reset(getInitialFormValues());
      }
    }
  }, [announcement, open, reset]);

  const handleFileUpload = async (event, lang) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const MAX_FILE_SIZE = 24 * 1024 * 1024; // 24 MB
    if (file.size > MAX_FILE_SIZE) {
      showErrorToaster("Upload file must be less than 24 MB");
      if (event?.target) event.target.value = "";
      return;
    }

    try {
      setUploadingMedia(true);
      const url = await AnnouncementService.uploadMedia(file);
      if (url) {
        setValue(`content.${lang}.mediaUrl`, url, { shouldValidate: true, shouldDirty: true });
        showSuccessToaster("Media uploaded successfully");
      } else {
        showErrorToaster("Failed to retrieve uploaded media URL");
      }
    } catch {
      showErrorToaster("Error uploading file");
    } finally {
      setUploadingMedia(false);
      if (event?.target) event.target.value = "";
    }
  };

  const onFormSubmit = (formData, submitStatus = "DRAFT") => {
    const isPublishing = submitStatus === "PUBLISHED";
    const payload = {
      content: formData.content,
      showButton: Boolean(formData.showButton),
      buttonUrl: formData.showButton ? formData.buttonUrl?.trim() || null : null,
      buttonTargetType: formData.buttonTargetType || "INTERNAL",
      publishedAt: isPublishing ? announcement?.publishedAt || new Date().toISOString() : announcement?.publishedAt || null,
      expiresAt: formData.expiresAt ? new Date(formData.expiresAt).toISOString() : null,
      priority: 2,
      status: submitStatus,
      otherContent: {
        allCompany: formData.allCompany,
        companyIds: formData.allCompany ? [] : (formData.companyIds || []).map(Number),
      },
    };

    if (isEdit) {
      dispatch(updateAnnouncement({ id: announcement.id, ...payload }));
    } else {
      dispatch(createAnnouncement(payload));
    }
  };

  const onFormError = (formErrors) => {
    if (formErrors?.content?.en?.title || formErrors?.content?.en?.message) {
      setActiveLangTab("en");
      showErrorToaster("Please fill in the required English title and message");
    } else if (formErrors?.buttonUrl) {
      showErrorToaster("Please provide a button link or disable Show Action Button");
    } else if (formErrors?.expiresAt) {
      showErrorToaster("Please provide a valid future expiration date");
    } else if (formErrors?.companyIds) {
      showErrorToaster("Please select at least one company or choose All Companies");
    } else {
      showErrorToaster("Please correct the form errors before submitting");
    }
  };

  const renderLanguageContentSection = (langConfig) => {
    const { code: lang, shortLabel, isRequired } = langConfig;
    const currentMediaType = watch(`content.${lang}.mediaType`) || "NONE";
    const currentMediaUrl = watch(`content.${lang}.mediaUrl`) || "";

    return (
      <Box key={lang} sx={{ display: activeLangTab === lang ? "block" : "none" }}>
        <Stack spacing={2.5}>
          <Controller
            control={control}
            name={`content.${lang}.title`}
            rules={isRequired ? { required: "Title is required" } : {}}
            render={({ field, fieldState: { invalid, error } }) => (
              <TextField
                {...field}
                label={`Title (${shortLabel})`}
                fullWidth
                size="small"
                error={Boolean(invalid)}
                helperText={error?.message || (isRequired ? "Displayed as the main headline" : "Optional translation")}
                required={isRequired}
                placeholder="e.g. New Platform Features Available Now"
              />
            )}
          />

          <Controller
            control={control}
            name={`content.${lang}.message`}
            rules={isRequired ? { required: "Message is required" } : {}}
            render={({ field, fieldState: { invalid, error } }) => (
              <TextField
                {...field}
                label={`Message (${shortLabel})`}
                fullWidth
                multiline
                rows={4}
                error={Boolean(invalid)}
                helperText={error?.message || (isRequired ? "Main announcement body text" : "Optional translation")}
                required={isRequired}
                placeholder="Write announcement details here..."
              />
            )}
          />

          {/* Button Label if Show Action Button is enabled */}
          {watchShowButton && (
            <Controller
              control={control}
              name={`content.${lang}.buttonLabel`}
              rules={isRequired ? { required: "Button label is required when button is enabled" } : {}}
              render={({ field, fieldState: { invalid, error } }) => (
                <TextField
                  {...field}
                  label={`Button Text (${shortLabel})${isRequired ? " *" : ""}`}
                  fullWidth
                  size="small"
                  error={Boolean(invalid)}
                  helperText={error?.message || `Button label shown to users (${shortLabel})`}
                  required={isRequired}
                  placeholder="e.g. Learn More, View Updates"
                />
              )}
            />
          )}

          {/* Media Section */}
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Controller
              control={control}
              name={`content.${lang}.mediaType`}
              render={({ field }) => (
                <FormControl component="fieldset" fullWidth>
                  <FormLabel component="legend" sx={{ fontSize: "0.85rem", fontWeight: 700, mb: 0.5, color: "text.primary" }}>
                    Media Attachment
                  </FormLabel>
                  <RadioGroup row {...field}>
                    <FormControlLabel value="NONE" control={<Radio size="small" />} label="None" />
                    <FormControlLabel value="IMAGE" control={<Radio size="small" />} label="Image" />
                    <FormControlLabel value="VIDEO" control={<Radio size="small" />} label="Video" />
                  </RadioGroup>
                </FormControl>
              )}
            />

            {/* Media Upload & URL Input */}
            {currentMediaType !== "NONE" && (
              <Box sx={{ mt: 2, pt: 2, borderTop: "1px dashed", borderColor: "divider" }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: "block", mb: 1 }}>
                  {currentMediaType === "IMAGE"
                    ? "Enter an image link or upload an image file (JPG, PNG, WEBP, GIF, max 24 MB)"
                    : "Enter a YouTube, Vimeo, or MP4 link, or upload a video file (max 24 MB)"}
                </Typography>

                <Stack spacing={1.5}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr auto" },
                      gap: 1.5,
                      alignItems: "flex-start",
                    }}
                  >
                    <Controller
                      control={control}
                      name={`content.${lang}.mediaUrl`}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label={currentMediaType === "IMAGE" ? "Image Link" : "Video Link"}
                          fullWidth
                          size="small"
                          placeholder={currentMediaType === "IMAGE" ? "https://example.com/banner.png" : "https://youtube.com/watch?v=... or .mp4"}
                        />
                      )}
                    />

                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      <Typography
                        sx={{
                          fontSize: "13px",
                          lineHeight: "28px",
                          fontWeight: 600,
                          visibility: "hidden",
                          display: { xs: "none", sm: "block" },
                        }}
                      >
                        Upload
                      </Typography>
                      <Button
                        variant="outlined"
                        component="label"
                        startIcon={uploadingMedia ? <CircularProgress size={16} /> : <CloudUploadIcon />}
                        disabled={uploadingMedia}
                        sx={{
                          whiteSpace: "nowrap",
                          height: 40,
                          textTransform: "none",
                          fontWeight: 600,
                          px: 2.5,
                        }}
                      >
                        {uploadingMedia ? "Uploading..." : `Upload ${currentMediaType === "IMAGE" ? "Image" : "Video"}`}
                        <input type="file" hidden accept={currentMediaType === "IMAGE" ? "image/*" : "video/*"} onChange={(e) => handleFileUpload(e, lang)} />
                      </Button>
                    </Box>
                  </Box>

                  {/* Media Preview Card */}
                  {Boolean(currentMediaUrl) && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        p: 1.2,
                        bgcolor: "grey.100",
                        borderRadius: 1.5,
                        border: "1px solid",
                        borderColor: "grey.300",
                      }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ overflow: "hidden" }}>
                        {currentMediaType === "IMAGE" ? (
                          <Box
                            component="img"
                            src={currentMediaUrl}
                            alt="Media Preview"
                            sx={{
                              width: 44,
                              height: 44,
                              borderRadius: 1,
                              objectFit: "cover",
                              bgcolor: "grey.300",
                              flexShrink: 0,
                            }}
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: 44,
                              height: 44,
                              borderRadius: 1,
                              bgcolor: "grey.900",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "white",
                              fontSize: "1.2rem",
                              flexShrink: 0,
                            }}
                          >
                            🎬
                          </Box>
                        )}
                        <Box sx={{ overflow: "hidden" }}>
                          <Typography variant="caption" fontWeight={700} noWrap sx={{ display: "block" }}>
                            Attached {currentMediaType === "IMAGE" ? "Image" : "Video"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block", maxWidth: { xs: 200, sm: 380 } }}>
                            {currentMediaUrl}
                          </Typography>
                        </Box>
                      </Stack>

                      <Button
                        size="small"
                        color="error"
                        startIcon={<DeleteOutlineIcon fontSize="small" />}
                        onClick={() => setValue(`content.${lang}.mediaUrl`, "", { shouldDirty: true })}
                        sx={{ textTransform: "none", fontWeight: 600, flexShrink: 0 }}
                      >
                        Remove
                      </Button>
                    </Box>
                  )}
                </Stack>
              </Box>
            )}
          </Box>
        </Stack>
      </Box>
    );
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid",
            borderColor: "divider",
            py: 2,
            px: 3,
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700}>
              {isEdit ? "Edit Announcement" : "Create Announcement"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Broadcast announcements and updates to employer portals
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Stack spacing={3} sx={{ mt: 3 }}>
            {/* Section 1: Multilingual Announcement Content */}
            <Box
              sx={{
                p: 2.5,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "grey.50",
              }}
            >
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={700} color="text.primary">
                  Announcement Content
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Provide announcement message in English (required) and optional translations (ES, PT, FR)
                </Typography>
              </Box>

              {/* Language Navigation Tabs: EN, ES, PT, FR */}
              <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2.5 }}>
                <Tabs value={activeLangTab} onChange={(e, val) => setActiveLangTab(val)} variant="scrollable" scrollButtons="auto">
                  {LANGUAGES.map((l) => (
                    <Tab key={l.code} label={l.label} value={l.code} sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.9rem" }} />
                  ))}
                </Tabs>
              </Box>

              {/* Localized Content Sections */}
              {LANGUAGES.map((l) => renderLanguageContentSection(l))}
            </Box>

            {/* Section 2: Action Button (CTA) */}
            <Box
              sx={{
                p: 2.5,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: watchShowButton ? 2 : 0 }}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700} color="text.primary">
                    Action Button
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Add a clickable call-to-action button to the announcement modal
                  </Typography>
                </Box>
                <Controller
                  control={control}
                  name="showButton"
                  render={({ field }) => (
                    <FormControlLabel control={<Switch checked={Boolean(field.value)} onChange={(e) => field.onChange(e.target.checked)} />} label="Show Action Button" sx={{ m: 0 }} />
                  )}
                />
              </Box>

              {watchShowButton && (
                <Box sx={{ pt: 1.5, borderTop: "1px dashed", borderColor: "divider" }}>
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "2fr 1fr" }, gap: 2, alignItems: "start" }}>
                    <Controller
                      control={control}
                      name="buttonUrl"
                      rules={{
                        required: "Button link is required when Action Button is enabled",
                      }}
                      render={({ field, fieldState: { invalid, error } }) => (
                        <TextField {...field} label="Button Link" size="small" fullWidth error={Boolean(invalid)} helperText={error?.message} placeholder="/settings or https://example.com" />
                      )}
                    />
                    <Controller
                      control={control}
                      name="buttonTargetType"
                      render={({ field }) => (
                        <TextField select fullWidth size="small" label="Open Link In" value={field.value || "INTERNAL"} onChange={(e) => field.onChange(e.target.value)}>
                          <MenuItem value="INTERNAL">Same Tab</MenuItem>
                          <MenuItem value="EXTERNAL">New Tab</MenuItem>
                        </TextField>
                      )}
                    />
                  </Box>
                </Box>
              )}
            </Box>

            {/* Section 3: Expiration Date */}
            <Box
              sx={{
                p: 2.5,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
              }}
            >
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="subtitle1" fontWeight={700} color="text.primary">
                  Expiration Date
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Announcement will automatically stop showing to users after this date and time
                </Typography>
              </Box>

              <Controller
                control={control}
                name="expiresAt"
                rules={{
                  required: "Expiration date is required",
                  validate: (val) => {
                    if (val && new Date(val) <= new Date()) {
                      return "Expiration date must be in the future";
                    }
                    return true;
                  },
                }}
                render={({ field, fieldState: { invalid, error } }) => (
                  <TextField
                    {...field}
                    label="Expires On *"
                    type="datetime-local"
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    error={Boolean(invalid)}
                    helperText={error?.message || "Defaults to 1 month from now"}
                  />
                )}
              />
            </Box>

            {/* Section 4: Target Audience */}
            <Box
              sx={{
                p: 2.5,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: watchAllCompany ? 0 : 2 }}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700} color="text.primary">
                    Target Audience
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Select which companies and their employers will receive this announcement
                  </Typography>
                </Box>
                <Controller
                  control={control}
                  name="allCompany"
                  render={({ field }) => (
                    <FormControlLabel control={<Switch checked={Boolean(field.value)} onChange={(e) => field.onChange(e.target.checked)} />} label="All Companies" sx={{ m: 0 }} />
                  )}
                />
              </Box>

              {!watchAllCompany && (
                <Box sx={{ pt: 1.5, borderTop: "1px dashed", borderColor: "divider" }}>
                  <Controller
                    control={control}
                    name="companyIds"
                    rules={{
                      validate: (val) => watchAllCompany || (Array.isArray(val) && val.length > 0) || "Please select at least one company",
                    }}
                    render={({ field, fieldState: { invalid, error } }) => {
                      const companyOptions = companies.map((c) => ({
                        value: c.id,
                        label: c.name || `Company #${c.id}`,
                      }));
                      const selectedValues = companyOptions.filter((opt) => (field.value || []).includes(opt.value));

                      return (
                        <FormControl fullWidth error={Boolean(invalid)}>
                          <Typography variant="caption" sx={{ mb: 0.5, fontWeight: 600 }}>
                            Select Target Companies *
                          </Typography>
                          <ReactSelect
                            isMulti
                            value={selectedValues}
                            onChange={(selected) => field.onChange((selected || []).map((s) => s.value))}
                            options={companyOptions}
                            placeholder="Search and choose companies..."
                            menuPortalTarget={document.body}
                            styles={reactSelectStyles}
                            menuPlacement={"top"}
                          />
                          {invalid && <FormHelperText>{error?.message}</FormHelperText>}
                        </FormControl>
                      );
                    }}
                  />
                </Box>
              )}
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            justifyContent: "space-between",
          }}
        >
          <Button startIcon={<VisibilityIcon />} onClick={() => setPreviewOpen(true)} color="secondary" variant="outlined" sx={{ textTransform: "none", fontWeight: 600 }}>
            Preview Modal
          </Button>

          <Stack direction="row" spacing={1.5}>
            <Button onClick={onClose} color="inherit" sx={{ textTransform: "none", fontWeight: 600 }}>
              Cancel
            </Button>
            <Button variant="outlined" onClick={handleSubmit((data) => onFormSubmit(data, "DRAFT"), onFormError)} disabled={submitting} sx={{ textTransform: "none", fontWeight: 600 }}>
              Save as Draft
            </Button>
            <Button variant="contained" onClick={handleSubmit((data) => onFormSubmit(data, "PUBLISHED"), onFormError)} disabled={submitting} sx={{ textTransform: "none", fontWeight: 600 }}>
              {submitting ? <CircularProgress size={20} color="inherit" /> : "Publish Now"}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>

      {/* Live Preview Modal */}
      <AnnouncementModalPreview open={previewOpen} onClose={() => setPreviewOpen(false)} announcement={{ ...formValues, priority: 2 }} />
    </>
  );
};

export default CreateEditAnnouncementModal;
